import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  MapPin,
  Package,
  Users,
  Truck,
  MessagesSquare,
  Wallet,
  Receipt,
  BarChart3,
  Bell,
  Settings,
  LifeBuoy,
  ChevronDown,
  UserCircle,
  Building2,
  Bike,
  ShieldCheck,
  UserRound,
  Satellite,
  ClipboardList,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { LogisticsIconsBg } from "./logistics-icons-bg";
import { useAuth } from "@/stores/auth";
import { usersApi } from "@/lib/api";

function profileInitials(name: string) {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

const primaryFull = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/app/tracking", label: "Live tracking", icon: MapPin },
  { to: "/app/deliveries", label: "Deliveries", icon: Package },
  { to: "/app/vehicles", label: "Vehicles", icon: Truck },
  { to: "/app/chat", label: "Conversations", icon: MessagesSquare, badge: 3 },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3 },
];

// Personal account holders manage their own deliveries and vehicles only —
// no driver/staff/corporate management, fleet-wide tracking, chat, or the
// internal support inbox. Deliveries/vehicles/analytics scoped server-side.
const primaryPersonal = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/app/deliveries", label: "Deliveries", icon: Package },
  { to: "/app/vehicles", label: "Vehicles", icon: Truck },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3 },
];

// A driver-only staff login is scoped server-side to its own deliveries/
// vehicle — no fleet-wide live tracking (which also lists every other
// driver), no analytics, no user directories. "My jobs" is added separately
// below (gated on isDriverOnly), not part of this list.
const primaryDriver = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/app/deliveries", label: "Deliveries", icon: Package },
  { to: "/app/vehicles", label: "Vehicles", icon: Truck },
  { to: "/app/chat", label: "Conversations", icon: MessagesSquare },
];

const usersMenuStaff = [
  { to: "/app/users/clients", label: "Clients", icon: UserCircle },
  { to: "/app/users/corporates", label: "Corporates", icon: Building2 },
  { to: "/app/users/personal", label: "Personal", icon: UserRound },
  { to: "/app/users/drivers", label: "Drivers", icon: Bike },
  { to: "/app/users/staff", label: "Staff", icon: ShieldCheck },
];

// Personal directory management and the staff roster are staff-only
// server-side (require_staff_claims in backend/src/routes/ops.rs) — a
// corporate login only gets its own fleet's drivers and its own client
// contacts (list_clients/list_drivers both scope to corporate_account_id).
const usersMenuCorporate = [
  { to: "/app/users/clients", label: "Clients", icon: UserCircle },
  { to: "/app/users/drivers", label: "Drivers", icon: Bike },
];

const finance = [
  { to: "/app/wallet", label: "Wallet", icon: Wallet },
  { to: "/app/transactions", label: "Transactions", icon: Receipt },
];

const utility = [
  { to: "/app/notifications", label: "Notifications", icon: Bell, badge: 2 },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

function NavItem({
  to,
  label,
  icon: Icon,
  exact,
  badge,
  nested,
}: {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  exact?: boolean;
  badge?: number;
  nested?: boolean;
}) {
  const { pathname } = useLocation();
  const active = exact ? pathname === to : pathname === to || pathname.startsWith(to + "/");
  return (
    <Link
      to={to}
      className={cn(
        "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
        nested && "ml-3 pl-4 border-l border-sidebar-border/60",
        active
          ? "bg-sidebar-accent text-sidebar-accent-foreground"
          : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
      )}
    >
      <Icon className={cn("h-4 w-4 shrink-0", active ? "text-sidebar-primary" : "text-sidebar-foreground/60 group-hover:text-sidebar-foreground")} />
      <span className="flex-1 truncate">{label}</span>
      {badge ? (
        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-sidebar-primary px-1.5 text-[10px] font-bold text-sidebar-primary-foreground">
          {badge}
        </span>
      ) : null}
    </Link>
  );
}

function UsersGroup({ items }: { items: typeof usersMenuStaff }) {
  const { pathname } = useLocation();
  const isUsersSection = pathname.startsWith("/app/users");
  const [open, setOpen] = useState(isUsersSection);
  const expanded = open || isUsersSection;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={expanded}
        className={cn(
          "group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          isUsersSection
            ? "bg-sidebar-accent text-sidebar-accent-foreground"
            : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
        )}
      >
        <Users className={cn("h-4 w-4 shrink-0", isUsersSection ? "text-sidebar-primary" : "text-sidebar-foreground/60 group-hover:text-sidebar-foreground")} />
        <span className="flex-1 text-left">Users</span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-180")} />
      </button>
      {expanded ? (
        <div className="mt-1 space-y-1">
          {items.map((i) => (
            <NavItem key={i.to} {...i} nested />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function AppSidebar() {
  const session = useAuth((s) => s.session);
  const kind = session?.kind;
  const isPersonal = kind === "personal";
  const isStaff = kind === "staff";
  const isCorporate = kind === "corporate";
  const isSuperadmin = isStaff && (session?.roles.includes("superadmin") ?? false);
  const roles = session?.roles ?? [];
  const isDriverOnly = isStaff && roles.length > 0 && roles.every((r) => r === "driver");
  const isDispatchCapable = isStaff && roles.some((r) => r === "superadmin" || r === "admin" || r === "dispatch_officer");
  const primary = isPersonal ? primaryPersonal : isDriverOnly ? primaryDriver : primaryFull;
  // Client/personal kinds have no directories to browse at all — the group
  // itself is hidden for them (see App.tsx CLIENT_BLOCKED_PREFIXES /
  // PERSONAL_BLOCKED_PREFIXES, both block /app/users entirely). A
  // driver-only staff login has the same restriction server-side
  // (DRIVER_ONLY_BLOCKED_PREFIXES in App.tsx) — no directories to browse.
  const usersMenu = isStaff && !isDriverOnly ? usersMenuStaff : isCorporate ? usersMenuCorporate : null;

  const [profile, setProfile] = useState<{ name: string; email: string } | null>(null);
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    usersApi
      .get(session.userId, session.accessToken)
      .then((u) => {
        if (!cancelled) setProfile({ name: u.full_name, email: u.email ?? u.phone ?? "" });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [session]);

  return (
    <aside className="hidden relative w-64 shrink-0 flex-col overflow-hidden border-r border-sidebar-border bg-sidebar bg-[radial-gradient(600px_300px_at_-20%_-10%,oklch(1_0_0/0.12),transparent_60%),radial-gradient(500px_260px_at_120%_110%,var(--blue-700),transparent_55%)] text-sidebar-foreground lg:flex">
      <LogisticsIconsBg />
      <Link to="/app/dashboard" className="group relative z-10 flex h-16 items-center gap-2.5 px-5 border-b border-sidebar-border">
        <div className="flex h-9 w-9 rotate-[-6deg] items-center justify-center rounded-xl bg-sidebar-primary text-sidebar-primary-foreground shadow-[2px_3px_0_0_var(--blue-900)] transition-transform duration-300 group-hover:rotate-0">
          <Package className="h-5 w-5" />
        </div>
        <div className="leading-tight">
          <div className="font-display text-lg font-semibold tracking-tight text-sidebar-accent-foreground">Parcelio</div>
          <div className="text-[11px] text-sidebar-foreground/70">Operator console</div>
        </div>
      </Link>

      <nav className="relative z-10 flex-1 space-y-6 overflow-y-auto px-3 py-5">
        <div className="space-y-1">
          <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/40">
            Operations
          </div>
          {primary.map((i) => (
            <NavItem key={i.to} {...i} />
          ))}
          {isDispatchCapable ? <NavItem to="/app/dispatch" label="Dispatch" icon={ClipboardList} /> : null}
          {isDriverOnly ? <NavItem to="/app/jobs" label="My jobs" icon={ClipboardList} /> : null}
          {usersMenu ? <UsersGroup items={usersMenu} /> : null}
          {isSuperadmin ? <NavItem to="/app/trackers" label="Trackers" icon={Satellite} /> : null}
        </div>
        <div className="space-y-1">
          <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/40">
            Finance
          </div>
          {finance.map((i) => (
            <NavItem key={i.to} {...i} />
          ))}
        </div>
        <div className="space-y-1">
          <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/40">
            Account
          </div>
          {utility.map((i) => (
            <NavItem key={i.to} {...i} />
          ))}
          {isStaff && !isDriverOnly ? <NavItem to="/app/support" label="Support inbox" icon={LifeBuoy} /> : null}
        </div>
      </nav>

      <div className="relative z-10 border-t border-sidebar-border p-3">
        <a
          href={`${(import.meta.env.VITE_SITE_URL as string | undefined) ?? ""}/contact`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
        >
          <LifeBuoy className="h-4 w-4" />
          Help & support
        </a>
        <Link
          to="/app/settings"
          className="mt-3 flex items-center gap-3 rounded-xl bg-sidebar-accent/50 p-3 transition-colors hover:bg-sidebar-accent"
        >
          <div className="flex h-9 w-9 shrink-0 rotate-[-4deg] items-center justify-center rounded-full bg-sidebar-primary text-sidebar-primary-foreground text-xs font-bold shadow-[1.5px_2px_0_0_var(--blue-900)]">
            {profile ? profileInitials(profile.name) || "?" : "…"}
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate text-sm font-medium text-sidebar-accent-foreground">{profile?.name ?? "Loading…"}</div>
            <div className="truncate text-[11px] text-sidebar-foreground/60">{profile?.email || "—"}</div>
          </div>
        </Link>
      </div>
    </aside>
  );
}
